import { FloatingSave } from '@components/form/FloatingSave'
import { Labeled } from '@components/form/Labeled'
import { Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import {
  CountryField,
  DegreeField,
  NationalityField
} from '@features/users/components/UsersFormSelects'
import { Home, User as UserIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { UserDetail } from 'types/api'
import { BaseParams } from 'types/utils'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Switch } from '@/components/ui/switch'

const TODAY_ISO = new Date().toISOString().slice(0, 10)

type LandlordsFormProps = {
  data?: UserDetail
  submitForm: (
    params: Omit<BaseParams<UserDetail, 'POST'>, 'body'> & { body: UserDetail }
  ) => void
}

export function LandlordsForm({ data, submitForm }: LandlordsFormProps) {
  const { t } = useTranslation('users')
  const isEdit = !!data
  const [showFloatingSave, setShowFloatingSave] = useState(false)

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<UserDetail>({
    mode: 'onTouched',
    defaultValues: data ?? {
      preferred_language: 'sk',
      is_active: true
    }
  })

  useEffect(() => {
    const sub = watch((_, { type }) => {
      if (type === 'change') setShowFloatingSave(true)
    })
    return () => sub.unsubscribe()
  }, [watch])

  const lenRule =
    (min: number, max: number) =>
    (v?: string | null) =>
      !v || (v.length >= min && v.length <= max) || t('form.validation.required')

  const invalidCls = (e?: unknown) => (e ? 'border-destructive' : '')

  function onValid(values: UserDetail) {
    setShowFloatingSave(false)
    // birth_date already arrives as an ISO `YYYY-MM-DD` string from DatePicker.
    submitForm({ body: { ...values, role: 'LANDLORD' } })
  }

  function discardChanges() {
    reset(data ?? { preferred_language: 'sk', is_active: true })
    setShowFloatingSave(false)
  }

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <div className="space-y-4">
        <Section
          key="profile"
          defaultOpen
          title={
            <SectionTitle
              icon={<UserIcon className="h-4 w-4" />}
              title={t('form.sections.personal')}
            />
          }
        >
          <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-3">
            <Controller
              control={control}
              name="preferred_language"
              render={({ field }) => (
                <Labeled label={t('form.labels.preferred_language')}>
                  <Combobox
                    searchable={false}
                    value={field.value ?? 'sk'}
                    onChange={field.onChange}
                    options={[
                      { label: t('form.labels.slovak'), value: 'sk' },
                      { label: t('form.labels.english'), value: 'en' },
                      { label: t('form.labels.ukrainian'), value: 'ua' }
                    ]}
                  />
                </Labeled>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_1fr_1fr] lg:col-span-3">
              <Controller
                control={control}
                name="degree"
                render={({ field }) => (
                  <DegreeField
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    error={errors.degree?.message}
                  />
                )}
              />

              <Labeled
                label={t('form.labels.firstName')}
                error={errors.first_name?.message}
              >
                <Input
                  className={invalidCls(errors.first_name)}
                  {...register('first_name', { validate: lenRule(2, 50) })}
                />
              </Labeled>

              <Labeled
                label={t('form.labels.lastName')}
                error={errors.last_name?.message}
              >
                <Input
                  className={invalidCls(errors.last_name)}
                  {...register('last_name', { validate: lenRule(2, 50) })}
                />
              </Labeled>
            </div>

            <Controller
              control={control}
              name="country"
              rules={{ required: t('form.validation.required') }}
              render={({ field }) => (
                <CountryField
                  required
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  error={errors.country?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="nationality"
              rules={{ required: t('form.validation.required') }}
              render={({ field }) => (
                <NationalityField
                  required
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  error={errors.nationality?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="birth_date"
              render={({ field }) => (
                <Labeled label={t('form.labels.birthDate')}>
                  <DatePicker
                    max={TODAY_ISO}
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                  />
                </Labeled>
              )}
            />

            <Labeled
              label={t('form.labels.idNumber')}
              required
              error={errors.id_number?.message}
            >
              <Input
                className={invalidCls(errors.id_number)}
                {...register('id_number', {
                  required: t('form.validation.idNumberFormat'),
                  validate: lenRule(5, 20)
                })}
              />
            </Labeled>

            <Controller
              control={control}
              name="passport_number"
              rules={{ validate: lenRule(5, 20) }}
              render={({ field }) => (
                <Labeled
                  label={t('form.labels.passportNumber')}
                  error={errors.passport_number?.message}
                >
                  <Input
                    className={invalidCls(errors.passport_number)}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                    onBlur={field.onBlur}
                  />
                </Labeled>
              )}
            />

            <Controller
              control={control}
              name="phone"
              rules={{ required: t('form.validation.phoneRequired') }}
              render={({ field }) => (
                <Labeled
                  label={t('form.labels.phone')}
                  required
                  error={errors.phone?.message}
                >
                  <PhoneInput
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    invalid={!!errors.phone}
                  />
                </Labeled>
              )}
            />

            <Labeled
              label="Email"
              required
              error={errors.email?.message}
              hint={isEdit ? t('form.emailLoginHint') : undefined}
            >
              <Input
                type="email"
                className={invalidCls(errors.email)}
                {...register('email', {
                  required: t('form.validation.required'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('form.validation.invalidEmail')
                  }
                })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.bankAccount')}
              error={errors.bank_account?.message}
            >
              <Input
                className={invalidCls(errors.bank_account)}
                {...register('bank_account', { validate: lenRule(10, 50) })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.bank')}
              error={errors.bank?.message}
            >
              <Input
                className={invalidCls(errors.bank)}
                {...register('bank', { validate: lenRule(2, 50) })}
              />
            </Labeled>
          </div>
        </Section>

        <Section
          key="address"
          defaultOpen
          title={
            <SectionTitle
              title={t('form.sections.address')}
              icon={<Home className="h-4 w-4" />}
            />
          }
        >
          <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-3">
            <Labeled
              label={t('form.labels.street')}
              required
              error={errors.street?.message}
            >
              <Input
                className={invalidCls(errors.street)}
                {...register('street', {
                  required: t('form.validation.streetRequired'),
                  validate: lenRule(2, 100)
                })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.address_number')}
              error={errors.address_number?.message}
            >
              <Input
                className={invalidCls(errors.address_number)}
                {...register('address_number', { validate: lenRule(1, 15) })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.address_orientation_number')}
              required
              error={errors.address_orientation_number?.message}
            >
              <Input
                className={invalidCls(errors.address_orientation_number)}
                {...register('address_orientation_number', {
                  required: t('form.validation.addressOrientationNumberRequired'),
                  validate: lenRule(1, 15)
                })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.city')}
              required
              error={errors.city?.message}
            >
              <Input
                className={invalidCls(errors.city)}
                {...register('city', {
                  required: t('form.validation.cityRequired'),
                  validate: lenRule(2, 100)
                })}
              />
            </Labeled>

            <Labeled
              label={t('form.labels.zip')}
              required
              error={errors.zip?.message}
            >
              <Input
                className={invalidCls(errors.zip)}
                {...register('zip', {
                  required: t('form.validation.zipRequired'),
                  validate: lenRule(2, 10)
                })}
              />
            </Labeled>

            <Controller
              control={control}
              name="is_active"
              render={({ field }) => (
                <Labeled label={t('form.labels.active')}>
                  <div className="flex h-10 items-center">
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </Labeled>
              )}
            />
          </div>
        </Section>

        {!isEdit && (
          <div className="flex justify-end">
            <Button type="submit">{t('form.buttons.save')}</Button>
          </div>
        )}
      </div>
      {isEdit && showFloatingSave && <FloatingSave discard={discardChanges} />}
    </form>
  )
}
